import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useProjectRecords } from "@/components/records/photo-grid/use-project-records";
import {
  usePendingRecords,
  type PendingRecordTile,
} from "@/hooks/use-pending-records";
import { usePendingAppendStatus } from "@/hooks/use-pending-appends";
import { resetPendingRecordForRetry } from "@/offline/db";
import type { RecordFilters } from "@/lib/query-keys";
import type { ProjectRecord } from "@/types/api";

/**
 * A single browsable record. Either a server-fetched record or a
 * pending/failed outbox tile (a local draft that has not yet synced). Outbox
 * cells come first so a freshly captured record is the first thing the user
 * sees — see [[photo-grid.spec]] § Pending and failed records.
 */
export type RecordCell =
  | { kind: "record"; record: ProjectRecord }
  | { kind: "outbox"; tile: PendingRecordTile };

/** Stable empty-array singleton so the no-data branch doesn't mint a new `[]`
 * each render — keeps consumers' effect deps stable. */
const EMPTY_RECORDS: ProjectRecord[] = [];

/**
 * Append-outbox state for a **synced** record with "+ Add" photos in flight.
 * `undefined` for an outbox (creating) cell — it has no server id to append to.
 */
export type AppendState = "pending" | "failed" | undefined;

/**
 * The records the photos page browses, in the order it browses them, plus
 * everything both presentations need to render that list: paging, the offline
 * cap, outbox sync state, and the retry action.
 *
 * Extracted so [[photo-grid.spec]] and [[photo-list.spec]] read one source
 * rather than each assembling their own — the two views must never disagree
 * about what a filter returns or which records are still pending, and both are
 * driven by the same query key, so switching views costs no refetch.
 */
export function useRecordCells(
  projectId: string,
  filters?: RecordFilters,
  /** Notified whenever the loaded server records change (new page, filter
   * change). Drives `totalCount` in the selection provider. */
  onItemsChange?: (records: ProjectRecord[]) => void,
) {
  const query = useProjectRecords(projectId, filters);

  // `data?.records` is stable across renders (memoized inside
  // useProjectRecords), and `EMPTY_RECORDS` is module-scoped, so the identity
  // is stable on both branches.
  const records = query.data?.records ?? EMPTY_RECORDS;

  // Local outbox: pending/failed records the user just captured, merged ahead
  // of the server records (newest-first inside the hook).
  // See [[offline-record-outbox]].
  const pendingTiles = usePendingRecords(projectId);

  // Which SYNCED records have "+ Add" photos still uploading (pending) or
  // permanently rejected (failed). The two sets are disjoint (failed wins).
  // See [[append-photo-outbox]].
  const { pendingRecordIds, failedRecordIds } =
    usePendingAppendStatus(projectId);

  const cells = useMemo<RecordCell[]>(
    () => [
      ...pendingTiles.map((tile): RecordCell => ({ kind: "outbox", tile })),
      ...records.map((record): RecordCell => ({ kind: "record", record })),
    ],
    [pendingTiles, records],
  );

  const queryClient = useQueryClient();

  // Tapping a failed tile/row resets it to `pending` and re-fires the drain.
  // `resetPendingRecordForRetry` flips the ENTRY back to `pending` AND, for a
  // multi-photo record, every `failed` photo back to `pending` (synced photos
  // are left as-is) so the re-drain re-uploads only what failed. Invalidating
  // the `["pendingRecords"]` PREFIX refreshes both this view (whose query is
  // `["pendingRecords", projectId]` — still prefix-matched) and the
  // always-mounted drain's `["pendingRecords"]` query, so it picks the record
  // back up. Never a popup — see [[offline-record-outbox]] § Permanent failure.
  const retryOutbox = useCallback(
    (tile: PendingRecordTile) => {
      void resetPendingRecordForRetry(tile.clientUuid).then(() => {
        void queryClient.invalidateQueries({ queryKey: ["pendingRecords"] });
      });
    },
    [queryClient],
  );

  const appendStateFor = useCallback(
    (cell: RecordCell): AppendState => {
      if (cell.kind !== "record") return undefined;
      if (failedRecordIds.has(cell.record.id)) return "failed";
      if (pendingRecordIds.has(cell.record.id)) return "pending";
      return undefined;
    },
    [failedRecordIds, pendingRecordIds],
  );

  // Surface the loaded record list back to the parent whenever it changes.
  // The callback is held in a ref (kept current by its own effect, never
  // written during render) so a parent that re-creates it each render doesn't
  // re-fire this on every paint — only an actual record change does.
  const onItemsChangeRef = useRef(onItemsChange);
  useEffect(() => {
    onItemsChangeRef.current = onItemsChange;
  }, [onItemsChange]);
  useEffect(() => {
    onItemsChangeRef.current?.(records);
  }, [records]);

  return {
    cells,
    records,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    offlineCapReached: query.offlineCapReached,
    cachedCount: query.cachedCount,
    totalCount: query.data?.totalCount ?? 0,
    isFilteredOffline: query.isFilteredOffline,
    retryOutbox,
    appendStateFor,
  };
}
